'use client';

import { FAQSection } from '@/types/sanity';
import { PortableText } from '@portabletext/react';
import { SectionHeader } from './SectionHeader';
import { SectionCTAs } from './SectionCTAs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ScrollReveal } from '@/components/animations/ScrollAnimations';
import { cn } from '@/lib/utils';

interface FAQSectionComponentProps {
  section: FAQSection;
  pageSlug?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toPlainText(blocks: any[]): string {
  return blocks
    .filter((b) => b._type === 'block')
    .map((b) => b.children?.map((c: { text?: string }) => c.text || '').join('') || '')
    .join('\n');
}

export function FAQSectionComponent({ section, pageSlug }: FAQSectionComponentProps) {
  const isTwoColumn = section.layout === 'two-column';
  const items = section.items || [];
  const midpoint = Math.ceil(items.length / 2);
  const leftItems = isTwoColumn ? items.slice(0, midpoint) : items;
  const rightItems = isTwoColumn ? items.slice(midpoint) : [];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: toPlainText(item.answer),
      },
    })),
  };

  const renderAccordion = (faqItems: typeof items) => (
    <Accordion type="single" collapsible className="w-full">
      {faqItems.map((item) => (
        <AccordionItem key={item._key} value={item._key}>
          <AccordionTrigger className="text-left text-base font-medium">
            {item.question}
          </AccordionTrigger>
          <AccordionContent>
            <div className="prose prose-sm max-w-none text-muted-foreground">
              <PortableText value={item.answer} />
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <section
        className={cn('faq-section py-8 md:py-12 lg:py-16', pageSlug && `faq-section--${pageSlug}`)}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="px-6 sm:px-0">
            <SectionHeader title={section.title} subtitle={section.subtitle} />

            {isTwoColumn ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                <div>{renderAccordion(leftItems)}</div>
                <div>{renderAccordion(rightItems)}</div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto">{renderAccordion(leftItems)}</div>
            )}

            <SectionCTAs ctas={section.ctas} />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
